import { animate, type AnimationPlaybackControls } from 'motion/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CHECKPOINT_SIZE } from '~/components/progress-column'
import { getCatchupScrollTop } from '~/helpers/get-catchup-scroll-top'
import {
  getCatchupDisplay,
  getCatchupTimeline,
} from '~/helpers/get-catchup-timeline'

/**
 * Post-jump catch-up phases. Timing/easing of the phase transitions follows
 * the Figma prototype's Smart Animate params (0.3s ease-out); only the
 * sprint's duration scales with the jump distance.
 *
 * - `approach`: interaction locks and the page scrolls back to the old
 *   score's position; the side light waves park at the viewport edges.
 * - `sprint`: the fill bar grows old → new, the bubble travels with its
 *   number rolling up, the page follows, and the comet trails the head.
 * - `settle`: the head rests at the new midpoint and the ending burst
 *   plays; the side waves retreat.
 */
export type CatchupPhase = 'idle' | 'approach' | 'sprint' | 'settle'

const APPROACH_DURATION_S = 0.3
/** Long enough for the burst + wave retreat (0.3s each) to read. */
const SETTLE_DURATION_MS = 650
/** Fraction of the viewport height the moving head is parked at. */
const HEAD_VIEWPORT_ANCHOR_RATIO = 0.45
/** The head's center offset inside its checkpoint-sized box. */
const HEAD_CENTER_OFFSET_PX = CHECKPOINT_SIZE / 2

interface CatchupState {
  phase: CatchupPhase
  /**
   * Rounded rolling score the page renders instead of the refetched data —
   * the pre-jump snapshot while armed, then the run's rolling value. Null
   * when idle.
   */
  displayScore: number | null
  /** Continuous px override for the fill bar / bubble top; null outside a run. */
  displayTop: number | null
  /** The pre-jump score snapshot; null when idle. */
  fromScore: number | null
}

const IDLE_STATE: CatchupState = {
  phase: 'idle',
  displayScore: null,
  displayTop: null,
  fromScore: null,
}

/**
 * Orchestrates the post-jump catch-up animation. Pure presentation: the
 * refetched activity is already the end state — `start` only performs the
 * old → new tween, exposing per-frame display overrides the page renders
 * instead of the raw data until the run settles back to `idle`. `arm`
 * pins the display to the pre-jump score before the end state can commit;
 * `start` then takes the override over into the run (or `disarm` unwinds
 * it when the jump fails).
 */
export function useCatchupAnimation() {
  const trackRef = useRef<HTMLDivElement | null>(null)
  const [state, setState] = useState<CatchupState>(IDLE_STATE)
  const runRef = useRef<{ stop: () => void } | null>(null)

  useEffect(() => () => runRef.current?.stop(), [])

  /**
   * Pins the display override to the pre-jump score, synchronously, before
   * the jump mutation's awaited invalidation can commit a render — the
   * refetched end state then lands behind the override instead of flashing.
   * Identity render until the refetch: the data still holds the old score.
   */
  const arm = useCallback((fromScore: number) => {
    setState({
      phase: 'idle',
      displayScore: fromScore,
      displayTop: null,
      fromScore,
    })
  }, [])

  /** Drops an armed override whose jump failed — back to the plain render. */
  const disarm = useCallback(() => {
    runRef.current?.stop()
    setState(IDLE_STATE)
  }, [])

  const start = useCallback(
    (input: {
      fromScore: number
      toScore: number
      checkpoints: number[]
      step: number
    }) => {
      runRef.current?.stop()
      const timeline = getCatchupTimeline(input)
      if (!timeline) {
        // No forward distance to animate — drop any armed override and
        // render the refetched state directly.
        setState(IDLE_STATE)
        return
      }

      let isStopped = false
      let controls: AnimationPlaybackControls | null = null
      let settleTimeout: number | null = null
      runRef.current = {
        stop: () => {
          isStopped = true
          controls?.stop()
          if (settleTimeout !== null) window.clearTimeout(settleTimeout)
        },
      }

      // The track's document offset is stable for the whole run — nothing
      // above it changes layout while interaction is locked.
      const trackTop =
        (trackRef.current?.getBoundingClientRect().top ?? 0) + window.scrollY
      const scrollTopFor = (top: number) =>
        getCatchupScrollTop({
          headDocumentY: trackTop + top + HEAD_CENTER_OFFSET_PX,
          viewportHeight: window.innerHeight,
          scrollHeight: document.documentElement.scrollHeight,
          anchorRatio: HEAD_VIEWPORT_ANCHOR_RATIO,
        })

      setState({
        phase: 'approach',
        displayScore: input.fromScore,
        displayTop: timeline.fromTop,
        fromScore: input.fromScore,
      })

      controls = animate(window.scrollY, scrollTopFor(timeline.fromTop), {
        duration: APPROACH_DURATION_S,
        ease: 'easeOut',
        onUpdate: (value) => window.scrollTo(0, value),
      })
      void controls.finished.then(() => {
        if (isStopped) return
        setState((previous) => ({ ...previous, phase: 'sprint' }))

        controls = animate(0, 1, {
          duration: timeline.durationS,
          ease: 'easeOut',
          onUpdate: (progress) => {
            const { top, score } = getCatchupDisplay(timeline, progress)
            window.scrollTo(0, scrollTopFor(top))
            setState((previous) => ({
              ...previous,
              displayScore: Math.round(score),
              displayTop: top,
            }))
          },
        })
        void controls.finished.then(() => {
          if (isStopped) return
          setState({
            phase: 'settle',
            displayScore: input.toScore,
            displayTop: timeline.toTop,
            fromScore: input.fromScore,
          })
          settleTimeout = window.setTimeout(() => {
            if (!isStopped) setState(IDLE_STATE)
          }, SETTLE_DURATION_MS)
        })
      })
    },
    [],
  )

  return {
    phase: state.phase,
    isActive: state.phase !== 'idle',
    displayScore: state.displayScore,
    displayTop: state.displayTop,
    fromScore: state.fromScore,
    trackRef,
    arm,
    disarm,
    start,
  }
}
